import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useCookies } from 'react-cookie';
import StickyBannerElement from './sticky-banner';

const StickyBanner = ({ title, subtitle, buttonText, buttonTarget }) => {
  const [bannerStuck, setBannerStuck] = useState(true);
  const [stickyBarPosition, setStickyBarPosition] = useState(null);
  const [stickyBarDismissed, setStickyBarDismissed] = useState(false);
  const [cookie, setCookie] = useCookies(['CookieControl']);
  const stickyBarRef = useRef(null);

  const getBarPositionDOM = () =>
    stickyBarRef.current.offsetTop + stickyBarRef.current.offsetHeight;

  // useEffect to monitor and reset on page resize
  useEffect(() => {
    if (stickyBarPosition === null) setStickyBarPosition(getBarPositionDOM());
    // On resize just remove the stickyBarPosition so it will calculate again on next scroll
    const handleResize = () => setStickyBarPosition(null);
    let resizeTimer;
    window.addEventListener('resize', () => {
      // Simple debounce
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => handleResize(), 200);
    });
    // Specify how to clean up after this effect:
    return () => window.removeEventListener('resize', handleResize);
  }, [stickyBarPosition]);

  // useEffect for monitoring sticky bar position within page against scroll
  useEffect(() => {
    if (Object.keys(cookie) && Object.keys(cookie).length === 0) {
      setBannerStuck(false);
      return;
    }

    const handleStickyBarScroll = () => {
      if (!stickyBarRef?.current) return;
      // Store the original position of the banner so we can monitor when within viewport
      if (stickyBarPosition === null) setStickyBarPosition(getBarPositionDOM());
      const scrollPosition = window.pageYOffset;
      const windowHeight = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight || 0
      );
      const bottomOfScreen = windowHeight + scrollPosition;
      const bannerWouldBeVisible = bottomOfScreen >= stickyBarPosition;
      if (bannerStuck && bannerWouldBeVisible) {
        setBannerStuck(false);
      } else if (!bannerStuck && !bannerWouldBeVisible) {
        setBannerStuck(true);
      }
    };
    handleStickyBarScroll();
    // Can't debounce due to the design of the sticky bar needing to stick at such a specific point
    window.addEventListener('scroll', handleStickyBarScroll);
    // Specify how to clean up after this effect:
    return () => window.removeEventListener('scroll', handleStickyBarScroll);
  }, [stickyBarPosition, bannerStuck, cookie]);

  return (
    <StickyBannerElement
      title={title}
      subtitle={subtitle}
      buttonText={buttonText}
      buttonTarget={buttonTarget}
      stickyBarRef={stickyBarRef}
      bannerStuck={!stickyBarDismissed && bannerStuck}
      dismissFn={setStickyBarDismissed}
    />
  );
};

StickyBanner.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  buttonText: PropTypes.string.isRequired,
  buttonTarget: PropTypes.string.isRequired,
};

export default StickyBanner;

export const StickyBannerAssembly = ({ contentfulData }) => (
  <StickyBanner
    title={contentfulData.title}
    subtitle={contentfulData.subTitle}
    buttonTarget={contentfulData.buttonTarget}
    buttonText={contentfulData.buttonText}
  />
);

StickyBannerAssembly.propTypes = {
  contentfulData: PropTypes.object,
};
